"use client"

import { useRouter } from '@/lib/router'
import {
  Bell,
  Check,
  CheckCheck,
  Clock,
  Timer,
  Trash2,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { useNotificationStore } from '@/stores/notification-store'
import { useComponentsTranslations } from '@/hooks/use-translations'
import type { AppNotification, NotificationType } from '@/types/notification'
import { formatDistanceToNow } from 'date-fns'
import { getDateLocale } from '@/lib/date-utils'
import { useLocale } from '@/lib/i18n-client'

// ── Icon per notification type ───────────────────────────────────────────────

function NotificationIcon({ type }: { type: NotificationType }) {
  switch (type) {
    case 'timer_complete':
      return <Timer className="h-4 w-4 text-orange-500" />
    case 'pomodoro_focus_end':
      return <Check className="h-4 w-4 text-green-500" />
    case 'pomodoro_break_end':
      return <Clock className="h-4 w-4 text-blue-500" />
    case 'task_upcoming':
      return <Bell className="h-4 w-4 text-yellow-500" />
    case 'task_overdue':
      return <Clock className="h-4 w-4 text-red-500" />
  }
}

// ── Single notification item ─────────────────────────────────────────────────

function NotificationItem({
  notification,
  onMarkRead,
  onDelete,
  onOpen,
}: {
  notification: AppNotification
  onMarkRead: (id: string) => void
  onDelete: (id: string) => void
  onOpen: (notification: AppNotification) => void
}) {
  const t = useComponentsTranslations()

  // Translate time (m,h ago)
  const locale = useLocale();
  const timeAgo = t('notif_center_time_ago', { time: formatDistanceToNow(new Date(notification.created_at), { locale: getDateLocale(locale) }) })

  return (
    <div
      className={cn(
        'flex items-start gap-3 px-4 py-3 transition-colors hover:bg-muted/50',
        !notification.read && 'bg-primary/5',
      )}
      onClick={() => onOpen(notification)}
    >
      <div className="mt-0.5 flex-shrink-0">
        <NotificationIcon type={notification.type} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p
            className={cn(
              'text-sm leading-tight',
              !notification.read ? 'font-medium' : 'text-muted-foreground',
            )}
          >
            {notification.title}
          </p>
          {!notification.read && (
            <div className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
          )}
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
          {notification.message}
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground/70">{timeAgo}</p>
      </div>

      <div className="flex flex-shrink-0 gap-1">
        {!notification.read && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={(e) => {
              e.stopPropagation()
              onMarkRead(notification.id)
            }}
            title={t('notif_center_mark_as_read')}
          >
            <Check className="h-3 w-3" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground hover:text-destructive"
          onClick={(e) => {
            e.stopPropagation()
            onDelete(notification.id)
          }}
          title={t('notif_center_delete')}
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    </div>
  )
}

// ── Empty state ──────────────────────────────────────────────────────────────

function EmptyNotifications() {
  const t = useComponentsTranslations()
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center w-[300px]">
      <Bell className="mb-3 h-10 w-10 text-muted-foreground/30" />
      <p className="text-sm font-medium text-muted-foreground">{t('notif_center_no_notifications')}</p>
      <p className="mt-1 text-xs text-muted-foreground/70">
        {t('notif_center_all_caught_up')}
      </p>
    </div>
  )
}

// ── Main component ───────────────────────────────────────────────────────────

export function NotificationCenter() {
  const router = useRouter()
  const t = useComponentsTranslations()
  const unreadCount = useNotificationStore((s) => s.unreadCount)
  const notifications = useNotificationStore((s) => s.notifications)
  const isOpen = useNotificationStore((s) => s.isOpen)
  const isLoading = useNotificationStore((s) => s.isLoading)
  const setIsOpen = useNotificationStore((s) => s.setIsOpen)
  const markAsRead = useNotificationStore((s) => s.markAsRead)
  const markAllAsRead = useNotificationStore((s) => s.markAllAsRead)
  const deleteNotification = useNotificationStore((s) => s.deleteNotification)
  const clearAll = useNotificationStore((s) => s.clearAll)
  const triggerId = "header-notification-trigger"
  const contentId = "header-notification-content"
  const openNotification = (notification: AppNotification) => {
    if (notification.deep_link) {
      router.push(notification.deep_link)
      setIsOpen(false)
    }
    if (!notification.read) {
      markAsRead(notification.id)
    }
  }

  // No refetch on open: the list is a live Convex subscription.

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild id={triggerId}>
        <Button variant="ghost" size="icon" className="relative h-9 w-9">
          <Bell className="h-[1.1rem] w-[1.1rem]" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-bold"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
          <span className="sr-only">{t('notif_center_title')}</span>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        id={contentId}
        align="end"
        className="w-fit p-0"
        sideOffset={8}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-sm font-semibold">{t('notif_center_title')}</h3>
          <div className="flex gap-1">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1.5 text-xs"
                onClick={() => markAllAsRead()}
              >
                <CheckCheck className="h-3.5 w-3.5" />
                {t('notif_center_mark_all_read')}
              </Button>
            )}
            {notifications.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1.5 text-xs text-muted-foreground hover:text-destructive"
                onClick={() => clearAll()}
              >
                <Trash2 className="h-3.5 w-3.5" />
                {t('notif_center_clear')}
              </Button>
            )}
          </div>
        </div>

        {/* Notification list */}
        <ScrollArea className="max-h-[400px]">
          {isLoading && notifications.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground/20 border-t-primary" />
            </div>
          ) : notifications.length === 0 ? (
            <EmptyNotifications />
          ) : (
            <div>
              {notifications.map((notification, index) => (
                <div key={notification.id}>
                  <NotificationItem
                    notification={notification}
                    onMarkRead={markAsRead}
                    onDelete={deleteNotification}
                    onOpen={openNotification}
                  />
                  {index < notifications.length - 1 && <Separator />}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  )
}
